import { FeatureFlag } from '~/lib/flags/flags';

export class RuntimeFeatureFlagStore {
  private overrides: Map<FeatureFlag, boolean> = new Map();
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.loadFromWindow();

    if (typeof window !== 'undefined') {
      (window as any).__setFeatureFlag = this.setFlag.bind(this);
      (window as any).__clearFeatureFlag = this.clearFlag.bind(this);
      (window as any).__getAllFeatureFlags = this.getAllFlags.bind(this);
      (window as any).__clearAllFeatureFlags = this.clearAllFlags.bind(this);
    }
  }

  clearAllFlags(): void {
    this.overrides.clear();
    this.syncToWindow();
    this.notifyListeners();
  }

  clearFlag(flag: FeatureFlag): void {
    this.overrides.delete(flag);
    this.syncToWindow();
    this.notifyListeners();
  }

  getAllFlags(): Record<string, boolean> {
    return Object.fromEntries(this.overrides.entries());
  }

  getFlag(flag: FeatureFlag): boolean | undefined {
    return this.overrides.get(flag);
  }

  hasFlag(flag: FeatureFlag): boolean {
    return this.overrides.has(flag);
  }

  setFlag(flag: FeatureFlag, value: boolean): void {
    this.overrides.set(flag, value);
    this.syncToWindow();
    this.notifyListeners();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private loadFromWindow(): void {
    if (typeof window === 'undefined') return;

    const windowFlags = (window as any).__cypressFeatureFlags;
    if (windowFlags && typeof windowFlags === 'object') {
      Object.entries(windowFlags).forEach(([flag, value]) => {
        if (typeof value === 'boolean') {
          this.overrides.set(flag as FeatureFlag, value);
        }
      });
      this.notifyListeners();
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }

  private syncToWindow(): void {
    if (typeof window !== 'undefined') {
      (window as any).__cypressFeatureFlags = this.getAllFlags();
    }
  }
}

/**
 * Lazy-loaded runtime store instance
 * Only creates the store when accessed, and only during Cypress testing
 */
let runtimeStoreInstance: RuntimeFeatureFlagStore | null = null;

export const getRuntimeStore = (): RuntimeFeatureFlagStore | null => {
  const isCypressTest = typeof window !== 'undefined' && (window as any).Cypress;
  if (!isCypressTest) {
    return null;
  }

  if (!runtimeStoreInstance) {
    runtimeStoreInstance = new RuntimeFeatureFlagStore();
  }

  return runtimeStoreInstance;
};
